import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import WizardPage from './pages/WizardPage';
import CustomersPage from './pages/CustomersPage';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import JsonDocumentEditorPage from './pages/JsonDocumentEditorPage';
import WorkspacePage from './pages/WorkspacePage';
import DebugAuthPage from './pages/DebugAuthPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TermsAcceptanceGate } from './components/TermsAcceptanceGate';
import { authService } from './services/auth-service';
import { AIProvider, useAI } from './contexts/AIContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { CommandPalette } from './components/CommandPalette';
import { ProjectSwitcher } from './components/ProjectSwitcher';
import { ProjectCreateModal } from './components/ProjectCreateModal';
import { CalculatorModal } from './components/calculator';
import { ToastProvider, ConfirmProvider, PromptProvider, ErrorBoundary } from './components/ui';
import './App.css';

// Protected Link component that checks for unsaved changes
// Available for future navigation with unsaved changes warning
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/rules-of-hooks */
function _ProtectedNavLink({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) {
  const { requestNavigation } = useAI();
  const navigate = useNavigate();
  
  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    const allowed = await requestNavigation(to);
    if (allowed) {
      navigate(to);
    }
  };
  
  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}
/* eslint-enable @typescript-eslint/no-unused-vars, react-hooks/rules-of-hooks */

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [_userEmail, setUserEmail] = useState('');
  const [userGroups, setUserGroups] = useState<string[]>([]);
  void _userEmail; // Available for future TopBar user display
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const { 
    openPagePanel, 
    activeProject, 
    switchProject,
    projectSwitcherOpen,
    openProjectSwitcher,
    closeProjectSwitcher,
    projectCreateModalOpen,
    openProjectCreateModal,
    closeProjectCreateModal,
  } = useAI();
  
  // Don't show header on login/onboard pages
  const isPublicPage = location.pathname === '/login' || location.pathname === '/onboard';

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const user = await authService.getCurrentUser();
      setIsAuthenticated(!!user);
      setUserEmail(user?.email || '');
      setUserGroups(user?.groups || []);
    } catch {
      setIsAuthenticated(false);
    }
  };

  const handleLogout = async () => {
    await authService.signOut();
    setIsAuthenticated(false);
    setUserEmail('');
    navigate('/login');
  };
  
  // Global keyboard listeners for command palette (Cmd+K) and project switcher (Cmd+P)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux) - Command Palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isAuthenticated && !isPublicPage) {
          setShowCommandPalette(true);
        }
      }
      // Cmd+P (Mac) or Ctrl+P (Windows/Linux) - Project Switcher
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault();
        if (isAuthenticated && !isPublicPage) {
          openProjectSwitcher();
        }
      }
      // Cmd+Shift+C (Mac) or Ctrl+Shift+C (Windows/Linux) - Calculator
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        if (isAuthenticated && !isPublicPage) {
          setShowCalculator(true);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openProjectSwitcher is stable callback from AIContext
  }, [isAuthenticated, isPublicPage]);
  
  // Handle navigation from command palette
  const handleCommandPaletteNavigate = useCallback((panelId: string) => {
    // Navigate to workspace if not already there
    if (!location.pathname.startsWith('/workspace')) {
      navigate('/workspace');
    }
    // Open the panel as a tab
    openPagePanel(panelId);
  }, [navigate, openPagePanel, location.pathname]);

  return (
    <div className="h-screen flex overflow-hidden bg-card transition-colors">
      {/* Command Palette (global, triggered by Cmd+K) */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={handleCommandPaletteNavigate}
        onNavigateToPath={(path) => navigate(path)}
        onLogout={handleLogout}
        onOpenCalculator={() => setShowCalculator(true)}
        userGroups={userGroups}
      />
      
      {/* Calculator Modal (global, triggered by Cmd+Shift+C) */}
      <CalculatorModal
        open={showCalculator}
        onOpenChange={setShowCalculator}
      />
      
      {/* Project Switcher (global, triggered by Cmd+P or Projects tab) */}
      <ProjectSwitcher
        isOpen={projectSwitcherOpen}
        onClose={closeProjectSwitcher}
        onSwitchProject={switchProject}
        activeProject={activeProject}
        onCreateProject={openProjectCreateModal}
        onBrowseAll={() => {
          // Close current project and open Projects panel
          switchProject(null);
          openPagePanel('projects');
        }}
      />
      
      {/* Project Create Modal (triggered from Project Switcher) */}
      {projectCreateModalOpen && (
        <ProjectCreateModal
          onClose={closeProjectCreateModal}
          onCreated={(newProject) => {
            closeProjectCreateModal();
            // Switch to the new project
            switchProject(newProject.id);
          }}
        />
      )}

      {/* Main Content Area - full width now that sidebar is removed */}
      <div className="flex-1 flex flex-col overflow-hidden relative z-0">
        {/* Main Content - Full height, no TopBar */}
        <main className="flex-1 overflow-y-auto">
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/onboard" element={<OnboardingPage />} />
          
          {/* Protected routes - Workspace is now the default/home route */}
          {/* All panel-based navigation (projects, references, etc.) is handled via openPagePanel in Workspace */}
          <Route path="/" element={<ProtectedRoute><TermsAcceptanceGate><WorkspacePage /></TermsAcceptanceGate></ProtectedRoute>} />
          <Route path="/workspace" element={<ProtectedRoute><TermsAcceptanceGate><WorkspacePage /></TermsAcceptanceGate></ProtectedRoute>} />
          <Route path="/workspace/:conversationId" element={<ProtectedRoute><TermsAcceptanceGate><WorkspacePage /></TermsAcceptanceGate></ProtectedRoute>} />
          
          {/* Wizard - special full-page experience for document generation */}
          <Route path="/wizard" element={<ProtectedRoute><TermsAcceptanceGate><WizardPage /></TermsAcceptanceGate></ProtectedRoute>} />
          
          {/* Document editors - opened from Workspace tabs */}
          <Route path="/company-documents/:documentId/edit" element={<ProtectedRoute><TermsAcceptanceGate><JsonDocumentEditorPage /></TermsAcceptanceGate></ProtectedRoute>} />
          <Route path="/projects/:projectId/documents/:documentNumber/edit" element={<ProtectedRoute><TermsAcceptanceGate><JsonDocumentEditorPage /></TermsAcceptanceGate></ProtectedRoute>} />
          
          {/* Admin routes */}
          <Route path="/customers" element={<ProtectedRoute requireAdmin><TermsAcceptanceGate><CustomersPage /></TermsAcceptanceGate></ProtectedRoute>} />
          
          {/* Debug route */}
          <Route path="/debug-auth" element={<ProtectedRoute><TermsAcceptanceGate><DebugAuthPage /></TermsAcceptanceGate></ProtectedRoute>} />
          
          {/* Catch-all redirect to Workspace - handles old bookmarked URLs */}
          <Route path="*" element={<ProtectedRoute><TermsAcceptanceGate><WorkspacePage /></TermsAcceptanceGate></ProtectedRoute>} />
        </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary
      section="Application"
      compact={false}
    >
      <Router>
        <ThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <PromptProvider>
                <NavigationProvider>
                  <AIProvider>
                    <AppContent />
                  </AIProvider>
                </NavigationProvider>
              </PromptProvider>
            </ConfirmProvider>
          </ToastProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
